<script setup>
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useWindowSize } from '@vueuse/core';
import wootConstants from 'dashboard/constants/globals';

const { updateUISettings, uiSettings } = useUISettings();
const { width: windowWidth } = useWindowSize();
const activeNavigation = ref('chat');
const notesOpen = ref(false);
const directosVisible = ref(false);
const navigationLabels = {
  navigation: 'Navegación Mi Marciana',
  chat: 'Chat',
  products: 'Productos',
  order: 'Pedido',
  customer: 'Clienta',
  notes: 'Notas',
};

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const customerNotes = computed(() =>
  contactId.value ? notesByContact.value(contactId.value) || [] : []
);
const noteCount = computed(() => customerNotes.value.length);

const currentAccountId = useMapGetter('getCurrentAccountId');
const isFeatureEnabledonAccount = useMapGetter(
  'accounts/isFeatureEnabledonAccount'
);

const showCopilotTab = computed(() =>
  isFeatureEnabledonAccount.value(currentAccountId.value, FEATURE_FLAGS.CAPTAIN)
);
const isSmallScreen = computed(
  () => windowWidth.value < wootConstants.SMALL_SCREEN_BREAKPOINT
);
const isContactSidebarOpen = computed(
  () => uiSettings.value.is_contact_sidebar_open
);
const isCopilotPanelOpen = computed(
  () => uiSettings.value.is_copilot_panel_open
);

const toggleConversationSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: !isContactSidebarOpen.value,
    is_copilot_panel_open: false,
  });
};

const openMiMarciana = view => {
  notesOpen.value = false;
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });

  /*
   * La barra ya no guarda ni decide qué pestaña está activa.
   * Solo manda la orden; Mi Marciana confirma después la vista real.
   */
  if (!view) return;

  window.setTimeout(() => {
    window.dispatchEvent(
      new CustomEvent('mi-marciana:navigate', { detail: { view } })
    );
  }, 0);
};

const handleMiMarcianaSection = event => {
  if (event.origin !== 'https://app.mimarcianaia.com') return;
  if (event.data?.type !== 'MI_MARCIANA_ACTIVE_SECTION') return;

  /*
   * Si el panel está cerrado, Chat es la única pestaña válida. Así una
   * confirmación tardía del iframe no puede volver a marcar Productos o
   * Clienta mientras estamos escribiendo en el chat.
   */
  if (!isContactSidebarOpen.value) return;

  const section = event.data.section;
  if (activeNavigation.value === 'notes' && section === 'customer') return;
  if (['products', 'orders', 'customer', 'notes'].includes(section)) {
    activeNavigation.value = section;
  }
};

const openNotes = () => {
  updateUISettings({
    is_contact_sidebar_open: true,
    is_copilot_panel_open: false,
  });
  notesOpen.value = !notesOpen.value;
  activeNavigation.value = notesOpen.value ? 'notes' : 'customer';
  window.setTimeout(() => {
    window.dispatchEvent(new CustomEvent('mi-marciana:open-contact-notes'));
  }, 0);
};

const handleNotesClosed = () => {
  notesOpen.value = false;
  if (activeNavigation.value === 'notes') activeNavigation.value = 'customer';
};

const handleDirectosVisibility = event => {
  directosVisible.value = Boolean(event.detail?.visible);
};

watch(
  contactId,
  id => {
    if (id) store.dispatch('contactNotes/get', { contactId: id });
  },
  { immediate: true }
);

onMounted(() => {
  window.addEventListener('message', handleMiMarcianaSection);
  window.addEventListener(
    'mi-marciana:contact-notes-closed',
    handleNotesClosed
  );
  window.addEventListener(
    'mi-marciana:directos-visibility',
    handleDirectosVisibility
  );
});

onBeforeUnmount(() => {
  window.removeEventListener('message', handleMiMarcianaSection);
  window.removeEventListener(
    'mi-marciana:contact-notes-closed',
    handleNotesClosed
  );
  window.removeEventListener(
    'mi-marciana:directos-visibility',
    handleDirectosVisibility
  );
});

const showChat = () => {
  notesOpen.value = false;
  activeNavigation.value = 'chat';
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: false,
  });
};

const handleConversationSidebarToggle = () => {
  if (isContactSidebarOpen.value) {
    showChat();
    return;
  }
  openMiMarciana();
};

const handleCopilotSidebarToggle = () => {
  updateUISettings({
    is_contact_sidebar_open: false,
    is_copilot_panel_open: true,
  });
};

const keyboardEvents = {
  'Alt+KeyO': {
    action: toggleConversationSidebarToggle,
  },
};
useKeyboardEvents(keyboardEvents);
</script>

<template>
  <ButtonGroup
    class="flex flex-col justify-center items-center absolute top-36 xl:top-24 ltr:right-2 rtl:left-2 bg-n-solid-2/90 backdrop-blur-lg border border-n-weak/50 rounded-full gap-1.5 p-1.5 shadow-sm transition-shadow duration-200 hover:shadow !z-20"
  >
    <Button
      v-tooltip.top="$t('CONVERSATION.SIDEBAR.CONTACT')"
      sm
      class="!rounded-full !bg-black !text-white transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        '!bg-neutral-800 active:shadow-sm': isContactSidebarOpen,
      }"
      icon="i-ph-user-bold"
      @click="handleConversationSidebarToggle"
    />
    <Button
      v-if="showCopilotTab"
      v-tooltip.bottom="$t('CONVERSATION.SIDEBAR.COPILOT')"
      ghost
      slate
      sm
      class="!rounded-full transition-all duration-[250ms] ease-out active:!scale-95 active:duration-75"
      :class="{
        'bg-n-alpha-2 !text-n-iris-9 active:!brightness-105 active:shadow-sm':
          isCopilotPanelOpen,
      }"
      icon="i-woot-captain"
      @click="handleCopilotSidebarToggle"
    />
  </ButtonGroup>

  <nav
    v-if="!directosVisible"
    class="mm-chat-bottom-nav"
    :class="{ 'mm-chat-bottom-nav--desktop': !isSmallScreen }"
    :aria-label="navigationLabels.navigation"
  >
    <button
      type="button"
      :class="{ 'is-active': activeNavigation === 'chat' }"
      :aria-label="navigationLabels.chat"
      @click="showChat"
    >
      <span class="i-ph-chat-circle-dots-bold" />
      <strong>{{ navigationLabels.chat }}</strong>
    </button>
    <button
      type="button"
      class="mm-notes-button"
      :class="{ 'is-active': activeNavigation === 'notes' }"
      :aria-label="navigationLabels.notes"
      @click="openNotes"
    >
      <span class="i-ph-note-pencil-bold" />
      <strong>{{ navigationLabels.notes }}</strong>
      <em v-if="noteCount" class="mm-note-count">{{ noteCount }}</em>
    </button>
    <button
      type="button"
      :class="{ 'is-active': activeNavigation === 'products' }"
      @click="openMiMarciana('products')"
    >
      <span class="i-ph-tag-bold" />
      <strong>{{ navigationLabels.products }}</strong>
    </button>
    <button
      type="button"
      :class="{ 'is-active': activeNavigation === 'orders' }"
      @click="openMiMarciana('orders')"
    >
      <span class="i-ph-shopping-cart-bold" />
      <strong>{{ navigationLabels.order }}</strong>
    </button>
    <button
      type="button"
      :class="{ 'is-active': activeNavigation === 'customer' }"
      @click="openMiMarciana('home')"
    >
      <span class="i-ph-user-bold" />
      <strong>{{ navigationLabels.customer }}</strong>
    </button>
  </nav>
</template>

<style scoped>
:global(:root) {
  --mm-chat-bottom-nav-space: calc(78px + env(safe-area-inset-bottom, 0px));
}

.mm-chat-bottom-nav {
  position: fixed;
  z-index: 45;
  left: 8px;
  right: 8px;
  bottom: calc(6px + env(safe-area-inset-bottom, 0px));
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 6px;
  padding: 6px;
  box-sizing: border-box;
  border: 1px solid rgb(228 228 231 / 0.9);
  border-radius: 16px;
  background: rgb(255 255 255 / 0.96);
  box-shadow: 0 10px 30px rgb(0 0 0 / 0.14);
  backdrop-filter: blur(14px);
}
.mm-chat-bottom-nav button {
  position: relative;
  min-width: 0;
  min-height: 52px;
  touch-action: manipulation;
  -webkit-tap-highlight-color: transparent;
  border: 0;
  border-radius: 11px;
  background: transparent;
  color: #303030;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 3px;
}
.mm-chat-bottom-nav button span {
  color: #f33f8f;
  font-size: 19px;
}
.mm-chat-bottom-nav button strong {
  font-size: 10px;
  line-height: 1;
}
.mm-chat-bottom-nav button.is-active {
  background: #fff1f7;
  box-shadow: inset 0 0 0 1px #f33f8f;
}

.mm-note-count {
  position: absolute;
  top: 4px;
  right: 7px;
  display: grid;
  place-items: center;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  border-radius: 999px;
  background: #f33f8f;
  color: white;
  font-size: 9px;
  font-style: normal;
  font-weight: 900;
  line-height: 1;
}

@media (min-width: 1024px) {
  .mm-chat-bottom-nav--desktop {
    left: auto;
    right: 0;
    bottom: 0;
    width: var(--mm-desktop-sidebar-width, 520px);
    padding: 6px 10px;
    border-right: 0;
    border-bottom: 0;
    border-radius: 16px 0 0 0;
  }
}
</style>
